"use client";

import { useParams, useRouter } from "next/navigation";
import { type Key, useMemo } from "react";
import { IcSphere } from "@jetblue/kit-icons/ic-sphere";
import { InputSelect } from "@jetblue/kit/input-select";

type LanguageSelectorLabels = {
  en: string;
  es: string;
  fr: string;
  language: string;
};

type LanguageSelectorClientProperties = {
  labels: LanguageSelectorLabels;
  languages: string[];
};

type PageParameters = {
  pathname?: string[];
  personalizations: string;
  language: string;
};

const getBaseOptions = (labels: LanguageSelectorLabels) => [
  { label: labels.en, lang: "en", value: "en" },
  { label: labels.es, lang: "es", value: "es" },
  { label: labels.fr, lang: "fr", value: "fr" },
];

export const handleChangeLanguage = ({
  languages,
  pageParameters,
  router,
  value,
}: {
  languages: string[];
  pageParameters: PageParameters;
  router: ReturnType<typeof useRouter>;
  value: Key | null;
}) => {
  if (
    value &&
    value !== pageParameters.language &&
    languages.includes(value.toString())
  ) {
    /**
     * TECH DEBT: We plan to set this cookie with our library once it handles language persistence.
     */
    document.cookie = `lang=${value.toString()}; Path=/; ${process.env.NODE_ENV === "development" ? "" : "Domain=.jetblue.com;"}`;

    const pathname = pageParameters.pathname
      ? `/${pageParameters.pathname.join("/")}`
      : "/";

    const redirectPathname =
      (value === "en" ? "" : `/${value.toString()}`) + pathname;

    router.push(redirectPathname);
  }
};
export const getCurrentLocale = (pathname = "") => {
  const path = pathname.split("/");
  return path[1] === "es" || path[1] === "fr" ? path[1] : "en";
};
export const LanguageSelectorClient = ({
  labels,
  languages,
}: LanguageSelectorClientProperties) => {
  const pageParameters = useParams<PageParameters>();
  const router = useRouter();
  const baseOptions = useMemo(() => getBaseOptions(labels), [labels]);

  return (
    <InputSelect
      Icon={IcSphere}
      iconClassName="size-4 stroke-royal-blue"
      label={labels.language}
      // eslint-disable-next-line @typescript-eslint/no-deprecated, sonarjs/deprecation -- will fix soon
      onSelectionChange={(value: Key | null) => {
        handleChangeLanguage({ languages, pageParameters, router, value });
      }}
      options={baseOptions}
      value={pageParameters.language}
      variant="simple"
    />
  );
};
